import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import moment from 'moment';
import api from '../utils/api';
import AssessmentBasisPicker from './AssessmentBasisPicker';

export default function SessionModal({ user, session, initialDate, initialStartTime, onClose, onSaved }) {
  const isTrainer = user?.role === 'trainer';
  const isEdit = !!session;
  const [form, setForm] = useState({
    schedule_name: '',
    session_type: 'training',
    force_present: false,
    assessment_basis: '',
    intakes: [],
    venue_id: '',
    trainer_id: '',
    session_dates: [moment().format('YYYY-MM-DD')],
    start_time: '',
    end_time: '',
  });
  const [dateInput, setDateInput] = useState('');
  const [venues, setVenues] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [intakeOptions, setIntakeOptions] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/api/venues'),
      api.get('/api/users/trainers'),
      api.get('/api/students/intakes'),
    ]).then(([v, t, i]) => {
      setVenues(v.data.data || []);
      const allTrainers = t.data.data || [];
      // Trainer only sees themselves in the dropdown
      setTrainers(isTrainer ? allTrainers.filter(x => x.id === user?.id || String(x.id) === String(user?.id)) : allTrainers);
      setIntakeOptions((i.data.data || []).map(intake => ({
        value: intake.intake_code,
        label: intake.intake_code,
        soc_date: intake.soc_date ? String(intake.soc_date).slice(0, 10) : null,
      })));
    }).catch(() => toast.error('Failed to load form data.'));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (isEdit) {
      const intakesArr = Array.isArray(session.intakes) ? session.intakes : [session.intakes];
      setForm({
        schedule_name: session.schedule_name || '',
        session_type: session.session_type === 'assessment' ? 'assessment' : 'training',
        force_present: !!session.force_present,
        assessment_basis: session.session_type === 'assessment' ? (session.assessment_basis || '') : '',
        intakes: intakesArr,
        venue_id: session.venue_id || '',
        trainer_id: session.trainer_id || '',
        session_dates: [
          session.session_date
            ? String(session.session_date).slice(0, 10)
            : moment().format('YYYY-MM-DD'),
        ],
        start_time: session.start_time ? String(session.start_time).slice(0, 5) : '',
        end_time: session.end_time ? String(session.end_time).slice(0, 5) : '',
      });
    } else {
      setForm({
        schedule_name: '',
        session_type: 'training',
        force_present: false,
        assessment_basis: '',
        intakes: [],
        venue_id: '',
        trainer_id: '',
        session_dates: [initialDate || moment().format('YYYY-MM-DD')],
        start_time: initialStartTime || '',
        end_time: '',
      });
    }
  }, [isEdit, session, initialDate, initialStartTime]);

  // Fetch the modules available for the currently selected intake(s) —
  // options carry both module_name (label) and module_code (value/right side).
  useEffect(() => {
    if (form.intakes.length === 0) {
      setModuleOptions([]);
      return;
    }
    setLoadingModules(true);
    api.get('/api/students/intake-modules', { params: { intakes: form.intakes.join(',') } })
      .then(res => {
        setModuleOptions((res.data.data || []).map(mod => ({
          value: mod.module_code,
          label: mod.module_name,
        })));
      })
      .catch(() => toast.error('Failed to load modules for the selected intake(s).'))
      .finally(() => setLoadingModules(false));
  }, [form.intakes]); // eslint-disable-line

  function set(field, value) { setForm(p => ({ ...p, [field]: value })); setError(''); }

  // Switching away from Assessment clears force_present and assessment_basis —
  // both are Assessment-only and shouldn't silently persist against a Training session.
  function setSessionType(value) {
    setForm(p => ({
      ...p,
      session_type: value,
      force_present: value === 'assessment' ? p.force_present : false,
      assessment_basis: value === 'assessment' ? p.assessment_basis : '',
    }));
    setError('');
  }

  function addDate() {
    if (!dateInput) return;
    setForm(p => p.session_dates.includes(dateInput)
      ? p
      : { ...p, session_dates: [...p.session_dates, dateInput].sort() });
    setDateInput('');
    setError('');
  }

  function removeDate(d) {
    setForm(p => ({ ...p, session_dates: p.session_dates.filter(x => x !== d) }));
    setError('');
  }

  // Real-time end time validation flag
  const endTimeInvalid = !!(form.end_time && form.start_time && form.end_time <= form.start_time);

  async function handleSubmit(e) {
    e.preventDefault();

    // Force trainer_id for trainer role
    const resolvedTrainerId = isTrainer ? user?.id : form.trainer_id;

    const missingFields = [
      !form.intakes.length && 'intake',
      !form.schedule_name && 'module',
      !form.venue_id && 'venue',
      !form.session_dates.length && 'date',
      !form.start_time && 'start time',
      !form.end_time && 'end time',
      !resolvedTrainerId && 'trainer',
    ].filter(Boolean);
    if (missingFields.length) {
      setError(`Please complete the required field${missingFields.length > 1 ? 's' : ''}: ${missingFields.join(', ')}.`);
      return;
    }
    if (endTimeInvalid) {
      setError('End time must be later than start time.');
      return;
    }

    setSaving(true);
    try {
      const trainerObj = trainers.find(t => String(t.id) === String(resolvedTrainerId));
      const { session_dates, ...rest } = form;
      const payload = {
        ...rest,
        trainer_id: resolvedTrainerId,
        trainer_name: trainerObj?.name || null,
        trainer_email: trainerObj?.email || null,
        trainer_salutation: trainerObj?.salutation || null,
        trainer_first_name: trainerObj?.first_name || null,
      };
      if (isEdit) {
        payload.session_date = session_dates[0];
        // Checks MS 365 room availability for the date server-side before saving;
        // save is rejected (409) if the room is unavailable.
        const res = await api.put(`/api/sessions/${session.id}`, payload);
        if (res.data?.calendarSynced === false) toast.warn(res.data.message);
        else toast.success(res.data?.message || 'Session updated.');
      } else {
        payload.session_dates = session_dates;
        // Server checks MS 365 room availability for every selected date and
        // only creates sessions if ALL dates are available — otherwise none are saved.
        const res = await api.post('/api/sessions', payload);
        toast.success(res.data?.message || 'Session created.');
      }
      onSaved();
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409) {
        setError(data?.message || 'One or more selected dates are not available. Please choose a different room, time, or date(s).');
      } else if (!err.response && err.request) {
        setError('The attendance API could not be reached. Check the server connection and try again.');
      } else {
        setError(data?.message || err.message || 'Failed to save session.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered session-modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className={`bi ${isEdit ? 'bi-pencil' : 'bi-plus-circle'} me-2`} />
              {isEdit ? 'Edit Session' : 'Create Attendance Session'}
            </h5>
            <button className="btn-close btn-close-white" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit} noValidate>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger inline-error mb-3">
                  <i className="bi bi-exclamation-circle me-2" />{error}
                </div>
              )}

              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Intake(s) <span className="text-danger">*</span></label>
                  <Select
                    isMulti
                    options={intakeOptions}
                    value={intakeOptions.filter(o => form.intakes.includes(o.value))}
                    onChange={opts => {
                      set('intakes', opts.map(o => o.value));
                      set('schedule_name', '');
                    }}
                    placeholder="Select one or more intakes…"
                    classNamePrefix="react-select"
                    formatOptionLabel={(option, { context }) => context === 'menu' ? (
                      <div className="d-flex justify-content-between align-items-center gap-3">
                        <span>{option.label}</span>
                        {option.soc_date && (
                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                            {moment(option.soc_date).format('DD MMM YYYY')}
                          </span>
                        )}
                      </div>
                    ) : option.label}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Module <span className="text-danger">*</span></label>
                  <Select
                    options={moduleOptions}
                    value={moduleOptions.find(o => o.value === form.schedule_name) || null}
                    onChange={opt => set('schedule_name', opt ? opt.value : '')}
                    placeholder={
                      form.intakes.length === 0
                        ? 'Select an intake first…'
                        : loadingModules
                          ? 'Loading modules…'
                          : moduleOptions.length === 0
                            ? 'No module is common to all selected intakes'
                            : 'Select a module…'
                    }
                    isDisabled={form.intakes.length === 0 || loadingModules || moduleOptions.length === 0}
                    isLoading={loadingModules}
                    classNamePrefix="react-select"
                    formatOptionLabel={(option, { context }) => context === 'menu' ? (
                      <div className="d-flex justify-content-between align-items-center gap-3">
                        <span>{option.label}</span>
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>{option.value}</span>
                      </div>
                    ) : option.label}
                  />
                  {form.intakes.length > 1 && !loadingModules && moduleOptions.length === 0 && (
                    <div className="form-text text-danger">
                      The selected intakes don't share a common module. Only modules assigned to every selected intake can be used for one session.
                    </div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">Venue <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.venue_id} onChange={e => set('venue_id', e.target.value)} required>
                    <option value="">Select venue…</option>
                    {venues.map(v => (
                      <option key={v.id} value={v.id}>{v.name}{v.is_outdoor ? '' : ''}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Trainer <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={isTrainer ? user?.id : form.trainer_id}
                    onChange={e => set('trainer_id', e.target.value)}
                    disabled={isTrainer}
                  >
                    <option value="">Select trainer…</option>
                    {trainers.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label">Date(s) <span className="text-danger">*</span></label>
                  {isEdit ? (
                    <input
                      type="date"
                      className="form-control"
                      value={form.session_dates[0] || ''}
                      onChange={e => set('session_dates', [e.target.value])}
                      required
                    />
                  ) : (
                    <>
                      <div className="d-flex gap-2 mb-2">
                        <input
                          type="date"
                          className="form-control"
                          value={dateInput}
                          onChange={e => setDateInput(e.target.value)}
                        />
                        <button type="button" className="btn btn-tmis-outline btn-sm text-nowrap" onClick={addDate} disabled={!dateInput}>
                          <i className="bi bi-plus-lg me-1" />Add Date
                        </button>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {form.session_dates.length === 0 && (
                          <span className="text-muted small">No dates selected yet — add at least one.</span>
                        )}
                        {form.session_dates.map((d, idx) => (
                          <span key={d} className={`badge date-chip date-chip-${idx % 8} d-flex align-items-center gap-2 py-2 px-3`}>
                            {moment(d).format('DD MMM YYYY')}
                            <button
                              type="button"
                              className="date-chip-remove"
                              aria-label={`Remove ${d}`}
                              onClick={() => removeDate(d)}
                            >
                              <i className="bi bi-x-lg" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="form-text">Each date creates a separate session. All dates must be available on the room's calendar before saving.</div>
                    </>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">Start Time <span className="text-danger">*</span></label>
                  <input type="time" className="form-control" value={form.start_time} onChange={e => set('start_time', e.target.value)} required />
                </div>
                {/* <div className="col-md-4">
                  <label className="form-label">End Time</label>
                  <input
                    type="time"
                    className={`form-control ${endTimeInvalid ? 'is-invalid' : ''}`}
                    value={form.end_time}
                    onChange={e => set('end_time', e.target.value)}
                  />
                  {endTimeInvalid && (
                    <div className="invalid-feedback">End time must be later than start time.</div>
                  )}
                  <div className="form-text">Optional — leave blank if open-ended.</div>
                </div> */}
                <div className="col-md-4">
                  <label className="form-label">End Time <span className="text-danger">*</span></label>
                  <input type="time" className="form-control" value={form.end_time} onChange={e => set('end_time', e.target.value)} required />
                </div>

                <div className="col-12">
                  <label className="form-label">Session Type <span className="text-danger">*</span></label>
                  <div className="session-type-choices" role="radiogroup" aria-label="Session type">
                    {[
                      { value: 'training', label: 'Training', icon: 'bi-mortarboard-fill' },
                      { value: 'assessment', label: 'Assessment', icon: 'bi-clipboard2-check-fill' },
                    ].map(opt => {
                      const active = form.session_type === opt.value;
                      return (
                        <label key={opt.value} className={`session-type-choice ${active ? 'is-selected' : ''}`}>
                          <input
                            type="radio"
                            name="session_type"
                            value={opt.value}
                            checked={active}
                            onChange={() => setSessionType(opt.value)}
                            className="session-type-radio"
                          />
                          <i className={`bi ${opt.icon}`} aria-hidden="true" />
                          <span className="fw-semibold">
                            {opt.label}
                          </span>
                          {active && <i className="bi bi-check-circle-fill ms-auto" aria-hidden="true" />}
                        </label>
                      );
                    })}
                  </div>

                  {form.session_type === 'assessment' && (
                    <div className="mt-3">
                      <label className="form-label">Assessment Basis</label>
                      <AssessmentBasisPicker
                        name="assessment_basis"
                        value={form.assessment_basis}
                        onChange={v => set('assessment_basis', v)}
                      />
                    </div>
                  )}

                  {form.session_type === 'assessment' && (
                    <div className="session-auto-present-panel">
                      <label className="session-toggle" htmlFor="force_present">
                        <input
                          className="session-toggle-input"
                          type="checkbox"
                          role="switch"
                          id="force_present"
                          checked={form.force_present}
                          onChange={e => set('force_present', e.target.checked)}
                        />
                        <span className="session-toggle-track" aria-hidden="true"><span /></span>
                      </label>
                      <label htmlFor="force_present" className="session-auto-present-copy">
                        <span className="fw-semibold">Mark all as Present on scan</span>
                        <br />
                        Skip the late/absent time-window rules for this session — any student who scans the QR is marked Present, no matter when. Students who never scan are still marked Absent.
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="submit" className="btn btn-tmis-primary d-flex align-items-center gap-2" disabled={saving || endTimeInvalid}>
                {saving && <span className="spinner-border spinner-border-sm" />}
                {isEdit ? 'Save Changes' : 'Create Session'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
